import { type IResourceApiContext, type BaseRequestOptions } from '../common'
import { type overridePaths as paths, type overrideComponents as components } from './overrides'
import { createApiRequest as _createApiRequest, setPathParams } from '../../utils/request'

export type ApiPath = Extract<keyof paths, string>
export type ApiComponent = keyof components

export type ApiMethod<T extends ApiPath> = keyof paths[T]
export type ApiResponseCode<T extends ApiPath, M extends ApiMethod<T>> = Extract<keyof (paths[T][M] extends { responses: infer U } ? U : {}), number>

export interface ApiSchemaSignature<T extends ApiPath, M extends ApiMethod<T>> {
  path: T
  method: M
}

function hasOwnProperty<X extends {}, Y extends PropertyKey>
(obj: X, prop: Y): obj is X & Record<Y, unknown> {
  return Object.prototype.hasOwnProperty.call(obj, prop)
}

export type ApiSchemaRequest<T extends ApiPath, M extends ApiMethod<T>, OVERRIDE = unknown> =
(paths[T][M] extends { requestBody: { content: { 'application/json': infer U } } } ? { data: OVERRIDE extends unknown ? U : OVERRIDE } : {})
& (paths[T][M] extends { parameters: { path: infer U } } ? { params: U } : {})
& (paths[T][M] extends { parameters: { query?: infer U } } ? { query?: U | any } : { query?: any })

export type ApiSchemaResponseType<T extends ApiPath, M extends ApiMethod<T>, CODE extends ApiResponseCode<T, M> = ApiResponseCode<T, M>> =
  paths[T][M] extends { responses: { [k in CODE]: { content: { 'application/json': infer U } } } } ? U : void

export interface ApiSchemaResponse<T extends ApiPath, M extends ApiMethod<T>, CODE extends ApiResponseCode<T, M> = ApiResponseCode<T, M>> {
  data: ApiSchemaResponseType<T, M, CODE>
  status: CODE
}

export function hasResponseStatus<T extends ApiPath, M extends ApiMethod<T>, CODE extends ApiResponseCode<T, M>> (response: ApiSchemaResponse<T, M, any>, code: CODE): response is ApiSchemaResponse<T, M, CODE> {
  return response.status === code
}

export async function createSchemaApiRequest<T extends ApiPath, M extends ApiMethod<T>> (
  config: {
    ctx: IResourceApiContext
    options?: BaseRequestOptions
  } & ApiSchemaSignature<T, M>
  & ApiSchemaRequest<T, M>
): Promise<ApiSchemaResponse<T, M, ApiResponseCode<T, M>>> {
  let path: string = config.path
  if (hasOwnProperty(config, 'params') && config.params != null && (typeof config.params === 'object')) {
    path = setPathParams(path, config.params)
  }
  const response = await _createApiRequest({
    ctx: config.ctx,
    options: config.options,
    path,
    method: config.method as string,
    data: hasOwnProperty(config, 'data') ? config.data : undefined,
    query: config.query
  })

  return {
    status: response.status as ApiResponseCode<T, M>,
    data: response.data
  }
}

export type { paths, components }
